/**
 * Athos-powered search results page.
 *
 * Athos decides WHICH products appear (query, filters, sort, pagination, merchandising).
 * Shopify only renders the existing product card markup: result parentIds are mapped to product
 * IDs and rendered by sections/athos-search-cards.liquid, in Athos's order.
 *
 * URL state mirrors the Athos API: ?q=zen&filter.author=Foo&sort.price=asc&page=2
 * Docs: https://docs.athoscommerce.com/reference/search-result-pages
 */
if (!customElements.get('athos-search')) {
  const escapeHtml = (value) =>
    String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  // Athos returns some text already HTML-escaped (e.g. "Zen &amp; Art"); decode before re-escaping.
  const decodeHtml = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value === null || value === undefined ? '' : value;
    return textarea.value;
  };

  const SHOW_MORE_LIMIT = 14;
  const CARD_LOOKUP_CHUNK = 8;
  // How each extra content account is shown under the products. Order comes from Athos.contentSources.
  const CONTENT_UI = {
    collections: { layout: 'cards', limit: 4, heading: 'collectionsHeading' },
    blog: { layout: 'cards', limit: 4, heading: 'articlesHeading' }
  };

  class AthosSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      if (!window.Athos) {
        console.error('Athos: core script not loaded, cannot render search results.');
        return;
      }

      this.strings = JSON.parse(this.dataset.strings || '{}');
      this.perPage = parseInt(this.dataset.perPage, 10) || 24;
      this.cardsUrl = this.dataset.cardsUrl;

      this.facetsEl = this.querySelector('[data-athos-facets]');
      this.facetsGroup = this.querySelector('[data-athos-facets-group]');
      this.facetsWrap = this.querySelector('[data-athos-facets-wrap]');
      this.activeEl = this.querySelector('[data-athos-active]');
      this.gridEl = this.querySelector('[data-athos-grid]');
      this.paginationEl = this.querySelector('[data-athos-pagination]');
      this.emptyEl = this.querySelector('[data-athos-empty]');
      this.countEl = this.querySelector('[data-athos-count]');
      this.sortEl = this.querySelector('[data-athos-sort-container]');
      this.messageEl = this.querySelector('[data-athos-message]');
      this.toolbarEl = this.querySelector('[data-athos-toolbar]');
      this.sidebarSlot = this.querySelector('[data-athos-sidebar-slot]');
      this.drawerBody = this.querySelector('[data-athos-drawer-body]');
      this.drawerTrigger = this.querySelector('[data-athos-drawer-trigger]');
      this.filterCountEl = this.querySelector('[data-athos-filter-count]');
      this.bannerEls = [...this.querySelectorAll('[data-athos-banner]')];
      this.contentEl = this.querySelector('[data-athos-content]');
      this.modeEl = this.querySelector('[data-athos-mode]');
      this.articleMode = null;
      this.articleRatio = this.dataset.articleRatio || '1.5';
      this.content = {};
      this.contentObservers = [];

      // One set of filter markup: it lives in the sidebar on wide screens and in the drawer below that.
      this.wideQuery = window.matchMedia('(min-width: 1280px)');
      this.placeFacets();
      this.wideQuery.addEventListener('change', () => this.placeFacets());

      this.openFacets = new Set();
      this.responseId = null;
      this.resultsByProduct = new Map();
      this.impressed = new Set();
      this.pendingImpressions = [];
      this.pendingBanners = [];
      this.impressedBanners = new Set();
      this.impressionTimer = null;
      this.observer = null;
      this.hasLoaded = false;
      this.facetsSeen = false;
      this.requestId = 0;
      this.controller = null;

      this.addEventListener('change', this.onChange.bind(this));
      // <drawer-component> moves itself to <body> when opened, taking the filters out of this element,
      // so the filter list listens for its own events too.
      this.facetsEl.addEventListener('change', this.onChange.bind(this));
      this.facetsEl.addEventListener('toggle', this.onToggle.bind(this), true);
      this.facetsGroup.addEventListener('click', this.onResultClick.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('toggle', this.onToggle.bind(this), true);
      this.addEventListener('click', this.onResultClick.bind(this));
      this.contentEl.addEventListener('click', this.onContentClick.bind(this));
      this.gridEl.addEventListener('submit', this.onResultAddToCart.bind(this), true);
      window.addEventListener('popstate', () => this.load());

      this.load();
    }

    placeFacets() {
      if (!this.facetsGroup || !this.sidebarSlot || !this.drawerBody) return;
      const target = this.wideQuery.matches ? this.sidebarSlot : this.drawerBody;
      if (this.facetsGroup.parentElement !== target) target.prepend(this.facetsGroup);
    }

    /* ---------- URL state ---------- */

    currentParams() {
      return new URLSearchParams(window.location.search);
    }

    /** A search URL for q that keeps other params on the page (e.g. the theme editor's oseid) but drops result state. */
    searchHref(q, extra) {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()]
        .filter((key) => key === 'page' || key === 'type' || key.startsWith('filter.') || key.startsWith('sort.'))
        .forEach((key) => url.searchParams.delete(key));
      url.searchParams.set('q', q);
      Object.keys(extra || {}).forEach((key) => url.searchParams.set(key, extra[key]));
      return url.pathname + url.search;
    }

    navigate(params, { scroll = true } = {}) {
      const query = params.toString();
      window.history.pushState({}, '', `${window.location.pathname}${query ? '?' + query : ''}`);
      this.load();
      if (scroll) this.scrollToTop();
    }

    scrollToTop() {
      const target = this.toolbarEl || this;
      const top = target.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }

    /* ---------- Data ---------- */

    async load() {
      const requestId = ++this.requestId;
      const urlParams = this.currentParams();
      const q = (urlParams.get('q') || '').trim();

      const params = {
        q,
        page: urlParams.get('page') || 1,
        resultsPerPage: this.perPage
      };
      for (const [key, value] of urlParams) {
        if (key.startsWith('filter.') || key.startsWith('sort.')) {
          (params[key] = params[key] || []).push(value);
        }
      }

      const hasConstraint = q || Object.keys(params).some((k) => k.startsWith('filter.'));
      if (!hasConstraint) {
        this.renderEmpty(false);
        return;
      }

      // Athos treats each in-page route change as a new page load.
      if (this.hasLoaded) window.Athos.newPageLoad();
      this.hasLoaded = true;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setBusy(true);

      // ?type=article shows every matching blog article instead of products
      if (urlParams.get('type') === 'article' && this.blogSource()) {
        try {
          await this.loadArticleMode(q, params.page, requestId);
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          if (requestId === this.requestId) this.renderError();
        } finally {
          if (requestId === this.requestId) this.setBusy(false);
        }
        return;
      }
      this.articleMode = null;
      this.modeEl.hidden = true;

      try {
        const data = await window.Athos.request('search', params, { signal: this.controller.signal });
        if (requestId !== this.requestId) return;

        const redirect = data.merchandising && data.merchandising.redirect;
        if (redirect) {
          window.AthosTracking && window.AthosTracking.redirect('search', data.responseId, redirect);
          window.location.replace(redirect);
          return;
        }

        const cardsHtml = await this.fetchCards(data.results || []);
        if (requestId !== this.requestId) return;

        this.render(data, params, cardsHtml);
        // each load (first view, filter, sort, page change) is a new set of results shown
        if (window.AthosTracking) window.AthosTracking.render('search', data.responseId);
        this.renderContent(params, requestId);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (requestId === this.requestId) this.renderError();
      } finally {
        if (requestId === this.requestId) this.setBusy(false);
      }
    }

    blogSource() {
      return (window.Athos.contentSources || []).find((source) => source.key === 'blog');
    }

    /** Articles-only results: every blog article matching the query, with pagination. */
    async loadArticleMode(q, page, requestId) {
      const source = this.blogSource();
      const data = await window.Athos.request(
        'search',
        { q, page, resultsPerPage: this.perPage },
        { signal: this.controller.signal, siteId: source.siteId }
      );
      if (requestId !== this.requestId) return;

      // product-only furniture is hidden in this view
      this.contentEl.innerHTML = '';
      this.activeEl.innerHTML = '';
      this.facetsEl.innerHTML = '';
      this.facetsWrap.hidden = true;
      if (this.drawerTrigger) this.drawerTrigger.hidden = true;
      this.bannerEls.forEach((el) => (el.hidden = true));
      this.messageEl.hidden = true;
      this.toolbarEl.hidden = true;

      const total = parseInt(data.pagination && data.pagination.totalResults, 10) || 0;
      this.articleMode = { siteId: source.siteId, responseId: data.responseId, results: data.results || [] };

      this.modeEl.hidden = false;
      const template = total === 1 ? this.strings.resultsOne : this.strings.resultsOther;
      this.modeEl.querySelector('[data-athos-mode-heading]').textContent = total
        ? `${this.strings.articlesPageHeading}: ${(template || '').replace(/^\d+/, total).replace('__TERMS__', q)}`
        : this.strings.articlesPageHeading;
      const link = this.modeEl.querySelector('[data-athos-mode-link]');
      link.textContent = this.strings.viewProducts;
      link.href = this.searchHref(q);

      if (window.AthosTracking && window.AthosTracking.forSite) {
        window.AthosTracking.forSite(source.siteId).render('search', data.responseId);
      }

      if (!total) {
        this.renderEmpty(true, q);
        return;
      }

      this.emptyEl.hidden = true;
      this.gridEl.hidden = false;
      this.gridEl.innerHTML = this.articleMode.results.map((result, index) => this.cardMarkup(result, index)).join('');
      this.renderPagination(data.pagination);

      // one impression for the page of articles once a third of it is visible
      if (this.observer) this.observer.disconnect();
      if (window.AthosTracking && window.AthosTracking.forSite && 'IntersectionObserver' in window) {
        const state = this.articleMode;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            window.AthosTracking.forSite(state.siteId).impression('search', state.responseId, state.results);
          },
          { threshold: 0.3 }
        );
        this.observer.observe(this.gridEl);
      }
    }

    async fetchCards(results) {
      // uid is the variant ID; parentId is the Shopify product ID the card is rendered from.
      const ids = [...new Set(results.map((result) => result.parentId || result.uid).filter(Boolean))];
      if (!ids.length) return '';

      // Shopify's search silently drops terms beyond ~10 in a single OR query, so look cards up in
      // small groups (in parallel) and stitch them back together in Athos's order.
      const chunks = [];
      for (let i = 0; i < ids.length; i += CARD_LOOKUP_CHUNK) chunks.push(ids.slice(i, i + CARD_LOOKUP_CHUNK));

      const parts = await Promise.all(
        chunks.map(async (chunk) => {
          const query = chunk.map((id) => `id:${id}`).join(' OR ');
          const url = `${this.cardsUrl}${this.cardsUrl.includes('?') ? '&' : '?'}q=${encodeURIComponent(query)}`;
          const text = await fetch(url).then((response) => response.text());
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('.athos-search-cards');
          return section ? section.innerHTML : '';
        })
      );
      const html = parts.join('');

      const rendered = (html.match(/data-product-id=/g) || []).length;
      if (rendered < ids.length) {
        console.warn(`Athos: ${ids.length - rendered} of ${ids.length} results had no matching Shopify product (not on the Online Store channel?).`);
      }
      return html;
    }

    /* ---------- Collections and blog (separate Athos accounts) ---------- */

    /** Rows under the products: first page of an unfiltered search only (filters are product-only). */
    async renderContent(params, requestId) {
      this.contentObservers.forEach((observer) => observer.disconnect());
      this.contentObservers = [];
      this.contentEl.innerHTML = '';
      this.content = {};

      const sources = window.Athos.contentSources || [];
      const hasFilters = Object.keys(params).some((key) => key.startsWith('filter.'));
      if (!sources.length || !params.q || hasFilters || Number(params.page) > 1) return;

      const responses = await Promise.all(
        sources.map((source) =>
          window.Athos.request('search', { q: params.q, resultsPerPage: CONTENT_UI[source.key].limit }, { siteId: source.siteId }).catch((error) => {
            // extra content is optional; never let it break the product results
            console.warn(`Athos: ${source.key} results unavailable`, error);
            return null;
          })
        )
      );
      if (requestId !== this.requestId) return;

      sources.forEach((source, index) => {
        const data = responses[index];
        if (!data || !(data.results || []).length) return;
        this.content[source.key] = { siteId: source.siteId, responseId: data.responseId, results: data.results };
        this.contentEl.insertAdjacentHTML('beforeend', this.contentSectionMarkup(source.key, data.results));
        // one render per content row shown, to that row's own Athos account
        if (window.AthosTracking && window.AthosTracking.forSite) {
          window.AthosTracking.forSite(source.siteId).render('search', data.responseId);
        }
      });

      this.observeContentImpressions();
    }

    contentSectionMarkup(key, results) {
      const ui = CONTENT_UI[key];
      const items = `<div class="f-grid f-grid-1-cols md:f-grid-2-cols lg:f-grid-4-cols f-grid--gap-medium">${results
        .map((result, index) => this.cardMarkup(result, index))
        .join('')}</div>`;
      const viewAll =
        key === 'blog'
          ? `<a class="btn--link" href="${escapeHtml(this.searchHref(this.currentParams().get('q') || '', { type: 'article' }))}">${escapeHtml(this.strings.viewAllArticles)}</a>`
          : '';
      return `
        <section class="athos-search__articles" data-athos-source="${escapeHtml(key)}">
          <div class="athos-search__articles-header flex items-center justify-between gap-4">
            <h2 class="h4 athos-search__articles-heading">${escapeHtml(this.strings[ui.heading] || '')}</h2>
            ${viewAll}
          </div>
          ${items}
        </section>`;
    }

    cardMarkup(result, index) {
      const image = decodeHtml(window.Athos.articleImage(result));
      const url = escapeHtml(result.url);
      const date = result.published_at ? new Date(result.published_at) : null;
      const dateText = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';
      return `
        <div class="f-column">
          <div class="article-card" data-athos-index="${index}">
            ${
              image
                ? `<div class="article-card__media blocks-radius">
                    <a href="${url}" class="block media-wrapper hover-wrapper" tabindex="-1" aria-hidden="true">
                      <motion-element class="block" data-motion="zoom-out-sm" style="--aspect-ratio: ${escapeHtml(this.articleRatio)};">
                        <img src="${escapeHtml(image)}" alt="" loading="lazy" class="motion-reduce hover-scale-up" is="image-lazy">
                      </motion-element>
                    </a>
                  </div>`
                : ''
            }
            <div class="article-card__content">
              ${dateText ? `<div class="article-card__date text-sm">${escapeHtml(dateText)}</div>` : ''}
              <h3 class="article-card__title text-limit-2-lines text-pcard-title">
                <a class="reversed-link" href="${url}">${escapeHtml(decodeHtml(result.name))}</a>
              </h3>
            </div>
          </div>
        </div>`;
    }

    /** One impression per row, once it is a third visible; each account gets its own event. */
    observeContentImpressions() {
      if (!window.AthosTracking || !window.AthosTracking.forSite || !('IntersectionObserver' in window)) return;
      this.contentEl.querySelectorAll('[data-athos-source]').forEach((section) => {
        const state = this.content[section.dataset.athosSource];
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            window.AthosTracking.forSite(state.siteId).impression('search', state.responseId, state.results);
          },
          { threshold: 0.3 }
        );
        observer.observe(section);
        this.contentObservers.push(observer);
      });
    }

    onContentClick(event) {
      if (!window.AthosTracking || !window.AthosTracking.forSite || !event.target.closest('a[href]')) return;
      const section = event.target.closest('[data-athos-source]');
      const item = event.target.closest('[data-athos-index]');
      if (!section || !item) return;
      const state = this.content[section.dataset.athosSource];
      window.AthosTracking.forSite(state.siteId).clickThrough('search', state.responseId, state.results[Number(item.dataset.athosIndex)]);
    }

    /* ---------- Tracking (impressions, clicks, add to cart) ---------- */

    trackResponse(data) {
      this.responseId = data.responseId;
      this.resultsByProduct = new Map((data.results || []).map((result) => [String(result.parentId || result.uid), result]));
      this.impressed = new Set();
      this.pendingImpressions = [];
      this.pendingBanners = [];
      this.impressedBanners = new Set();
      if (this.observer) this.observer.disconnect();
      if (!window.AthosTracking || !('IntersectionObserver' in window)) return;

      // an impression is a card at least half visible; each product counts once per response
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.observer.unobserve(entry.target);
            const bannerUid = entry.target.dataset.bannerUid;
            if (entry.target.hasAttribute('data-athos-banner')) {
              if (bannerUid && !this.impressedBanners.has(bannerUid)) {
                this.impressedBanners.add(bannerUid);
                this.pendingBanners.push({ uid: bannerUid });
              }
              return;
            }
            const id = entry.target.dataset.productId;
            if (this.impressed.has(id) || !this.resultsByProduct.has(id)) return;
            this.impressed.add(id);
            this.pendingImpressions.push(this.resultsByProduct.get(id));
          });
          this.scheduleImpressionFlush();
        },
        { threshold: 0.5 }
      );
      this.gridEl.querySelectorAll('[data-product-id]').forEach((card) => this.observer.observe(card));
      this.bannerEls.filter((el) => !el.hidden).forEach((el) => this.observer.observe(el));
    }

    scheduleImpressionFlush() {
      if (!this.pendingImpressions.length && !this.pendingBanners.length) return;
      clearTimeout(this.impressionTimer);
      const responseId = this.responseId;
      this.impressionTimer = setTimeout(() => {
        const results = this.pendingImpressions.splice(0);
        const banners = this.pendingBanners.splice(0);
        window.AthosTracking.impression('search', responseId, results, banners);
      }, 500);
    }

    resultForEvent(event) {
      const card = event.target.closest('[data-product-id]');
      return card ? this.resultsByProduct.get(card.dataset.productId) : null;
    }

    onResultClick(event) {
      if (this.articleMode) {
        const item = event.target.closest('[data-athos-index]');
        if (item && event.target.closest('a[href]') && window.AthosTracking && window.AthosTracking.forSite) {
          const state = this.articleMode;
          window.AthosTracking.forSite(state.siteId).clickThrough('search', state.responseId, state.results[Number(item.dataset.athosIndex)]);
        }
        return;
      }
      // the filters + left banner move into the drawer (outside this element), so they listen themselves
      if (event.currentTarget === this && this.facetsGroup.contains(event.target)) return;
      if (!window.AthosTracking) return;
      const link = event.target.closest('a[href]');
      if (!link) return;
      const banner = event.target.closest('[data-athos-banner]');
      if (banner && banner.dataset.bannerUid) {
        window.AthosTracking.clickThrough('search', this.responseId, null, banner.dataset.bannerUid);
        return;
      }
      const result = this.resultForEvent(event);
      if (result) window.AthosTracking.clickThrough('search', this.responseId, result);
    }

    onResultAddToCart(event) {
      if (!window.AthosTracking) return;
      const result = this.resultForEvent(event);
      if (result) window.AthosTracking.addToCart('search', this.responseId, result, 1);
    }

    setBusy(isBusy) {
      this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    /* ---------- Rendering ---------- */

    render(data, params, cardsHtml) {
      const pagination = data.pagination || {};
      const total = parseInt(pagination.totalResults, 10) || 0;
      const hidden = (data.merchandising && data.merchandising.facetsHide) || [];
      const facets = (data.facets || []).filter((facet) => !hidden.includes(facet.field));

      this.renderMessage(data, params.q);
      this.renderBanners(data.merchandising && data.merchandising.content);

      if (!total) {
        this.renderEmpty(true, params.q);
        // keep filters visible so a shopper can back out of an over-filtered search
        this.renderFacets(facets);
        this.renderActive(data.filterSummary || []);
        return;
      }

      this.emptyEl.hidden = true;
      this.gridEl.hidden = false;
      this.toolbarEl.hidden = false;
      this.gridEl.innerHTML = cardsHtml;
      this.trackResponse(data);

      this.renderCount(total, params.q);
      this.renderSort(data.sorting);
      this.renderFacets(facets);
      this.renderActive(data.filterSummary || []);
      this.renderPagination(pagination);
    }

    /** Merchandising banners (header / banner / footer / left) come back as ready-made HTML from Athos. */
    renderBanners(content) {
      const slots = content || {};
      this.bannerEls.forEach((el) => {
        const html = [].concat(slots[el.dataset.athosBanner] || []).join('');
        el.innerHTML = html;
        el.hidden = !html.trim();
        el.dataset.bannerUid = (html.match(/data-banner-id="(\d+)"/) || [])[1] || '';
      });
    }

    renderMessage(data, q) {
      if (!this.messageEl) return;
      const query = data.query || {};
      if (query.corrected && query.original) {
        this.messageEl.hidden = false;
        this.messageEl.textContent = (this.strings.corrected || '')
          .replace('__CORRECTED__', query.corrected)
          .replace('__ORIGINAL__', query.original);
      } else {
        this.messageEl.hidden = true;
        this.messageEl.textContent = '';
      }
    }

    renderCount(total, q) {
      if (!this.countEl) return;
      // The locale string is pluralised server-side with a stand-in count (1 or 2) as its leading
      // number; swap that for the real total.
      const template = total === 1 ? this.strings.resultsOne : this.strings.resultsOther;
      this.countEl.textContent = (template || '').replace(/^\d+/, total).replace('__TERMS__', q);
    }

    renderSort(sorting) {
      if (!this.sortEl) return;
      const options = (sorting && sorting.options) || [];
      if (!options.length) {
        this.sortEl.innerHTML = '';
        return;
      }

      const params = this.currentParams();
      let selected = '';
      for (const [key, value] of params) {
        if (key.startsWith('sort.')) selected = `${key.slice(5)}:${value}`;
      }

      const items = options
        .map((option) => {
          const value = `${option.field}:${option.direction}`;
          return `<option value="${escapeHtml(value)}"${value === selected ? ' selected' : ''}>${escapeHtml(option.label)}</option>`;
        })
        .join('');

      this.sortEl.innerHTML = `
        <label for="AthosSortBy" class="text-nowrap font-body-bolder">${escapeHtml(this.strings.sortBy)}</label>
        <div class="select w-auto facet-short flex items-center gap-2 flex-grow">
          <select id="AthosSortBy" class="form-control form-control--select" data-athos-sort>
            <option value=""${selected ? '' : ' selected'}>${escapeHtml(this.strings.defaultSort)}</option>
            ${items}
          </select>
          ${this.strings.caretDownIcon || ''}
        </div>`;
    }

    renderFacets(facets) {
      if (!this.facetsEl) return;
      if (!facets.length) {
        this.facetsEl.innerHTML = '';
        this.facetsWrap.hidden = true;
        if (this.drawerTrigger) this.drawerTrigger.hidden = true;
        return;
      }
      this.facetsWrap.hidden = false;
      if (this.drawerTrigger) this.drawerTrigger.hidden = false;

      if (!this.facetsSeen) {
        // first render: honour the expanded/collapsed state Athos returns
        facets.forEach((facet) => {
          if (Number(facet.collapse) === 0) this.openFacets.add(facet.field);
        });
        this.facetsSeen = true;
      }

      this.facetsEl.innerHTML = `<form class="face-form" data-athos-facet-form>${facets
        .map((facet, index) => this.facetMarkup(facet, index))
        .join('')}</form>`;
    }

    facetMarkup(facet, index) {
      const open = this.openFacets.has(facet.field);
      const body = facet.type === 'slider' ? this.sliderMarkup(facet) : this.valuesMarkup(facet, index);
      return `
        <details class="accordion-details facet-accordion-details" is="accordion-details" data-facet="${escapeHtml(facet.field)}"${open ? ' open' : ''}>
          <summary class="accordion-details__summary flex items-center justify-between">
            <span class="h4">${escapeHtml(facet.label)}</span>
            ${this.strings.plusIcon || ''}
          </summary>
          <div class="accordion-details__content parent-display flex flex-col gap-3">${body}</div>
        </details>`;
    }

    valuesMarkup(facet, facetIndex) {
      const values = facet.values || [];
      const isRange = (value) => value.type === 'range';
      const hierarchy = facet.type === 'hierarchy';
      const single = facet.multiple === 'single' || hierarchy;
      const delimiter = facet.hierarchyDelimiter || '/';

      const items = values
        .map((value, i) => {
          const id = `athos-facet-${facetIndex}-${i}`;
          const name = isRange(value) ? `filter.${facet.field}.range` : `filter.${facet.field}`;
          const dataRange = isRange(value) ? ` data-low="${escapeHtml(value.low)}" data-high="${escapeHtml(value.high)}"` : '';
          const inputValue = isRange(value) ? `${value.low}-${value.high}` : value.value;
          return `
            <li class="flex items-center gap-3${i >= SHOW_MORE_LIMIT ? ' show-more-item hidden' : ''}"${hierarchy ? ` style="padding-inline-start:${Math.max(String(value.value).split(delimiter).length - 1, 0)}rem"` : ''}>
              <input class="checkbox" type="checkbox" id="${id}" name="${escapeHtml(name)}" value="${escapeHtml(inputValue)}"${dataRange}${single ? ' data-single' : ''}${value.active ? ' checked' : ''}>
              <label for="${id}" class="flex items-center gap-1">
                ${escapeHtml(value.label)}
                <span class="text-sm${value.active ? '' : ' text-subtext'}">(${escapeHtml(value.count)})</span>
              </label>
            </li>`;
        })
        .join('');

      const showMore =
        values.length > SHOW_MORE_LIMIT
          ? `<show-more-button>
              <button class="button-show-more btn--link" type="button">
                <span class="label-show-more label-text"><span aria-hidden="true">+ </span>${escapeHtml(this.strings.showMore)}</span>
                <span class="label-show-less label-text hidden"><span aria-hidden="true">- </span>${escapeHtml(this.strings.showLess)}</span>
              </button>
            </show-more-button>`
          : '';

      return `<ul class="grid gap-3">${items}</ul>${showMore}`;
    }

    sliderMarkup(facet) {
      const [min, max] = facet.range || [];
      const [low, high] = facet.active || [];
      const params = this.currentParams();
      const hasLow = params.has(`filter.${facet.field}.low`);
      const hasHigh = params.has(`filter.${facet.field}.high`);
      const step = facet.step || 1;
      return `
        <div class="athos-search__range-inputs">
          <input class="form-control" type="number" inputmode="numeric" data-range="low" data-field="${escapeHtml(facet.field)}"
            aria-label="${escapeHtml(this.strings.from)}" min="${escapeHtml(min)}" max="${escapeHtml(max)}" step="${escapeHtml(step)}"
            placeholder="${escapeHtml(min)}"${hasLow ? ` value="${escapeHtml(low)}"` : ''}>
          <span>${escapeHtml(this.strings.to)}</span>
          <input class="form-control" type="number" inputmode="numeric" data-range="high" data-field="${escapeHtml(facet.field)}"
            aria-label="${escapeHtml(this.strings.to)}" min="${escapeHtml(min)}" max="${escapeHtml(max)}" step="${escapeHtml(step)}"
            placeholder="${escapeHtml(max)}"${hasHigh ? ` value="${escapeHtml(high)}"` : ''}>
        </div>`;
    }

    renderActive(summary) {
      if (this.filterCountEl) {
        this.filterCountEl.hidden = !summary.length;
        this.filterCountEl.textContent = summary.length ? `(${summary.length})` : '';
      }
      if (!this.activeEl) return;
      if (!summary.length) {
        this.activeEl.innerHTML = '';
        return;
      }

      const chips = summary
        .map((item) => {
          const label = item.filterValue ? `${item.filterLabel ? item.filterLabel + ': ' : ''}${item.filterValue}` : item.label;
          return `
            <button type="button" class="facet-remove btn btn--secondary flex relative" data-athos-remove data-field="${escapeHtml(item.field)}" data-value="${escapeHtml(item.value)}"
              aria-label="${escapeHtml(this.strings.clear.replace('__NAME__', label))}">
              <div class="flex items-center justify-between gap-3">
                <div class="facet-title text-wrap flex items-center gap-2">${escapeHtml(label)}</div>
                ${this.strings.closeIcon || ''}
              </div>
            </button>`;
        })
        .join('');

      const clearAll =
        summary.length > 1
          ? `<button type="button" class="facet-clear-all btn--link" data-athos-clear-all>${escapeHtml(this.strings.clearAll)}</button>`
          : '';
      this.activeEl.innerHTML = chips + clearAll;
    }

    renderPagination(pagination) {
      if (!this.paginationEl) return;
      const current = parseInt(pagination.currentPage, 10) || 1;
      const total = parseInt(pagination.totalPages, 10) || 1;
      if (total <= 1) {
        this.paginationEl.innerHTML = '';
        return;
      }

      const pages = [];
      for (let p = 1; p <= total; p++) {
        if (p === 1 || p === total || Math.abs(p - current) <= 2) pages.push(p);
        else if (pages[pages.length - 1] !== '…') pages.push('…');
      }

      const link = (page, label, classes, aria) =>
        `<li><a href="${escapeHtml(this.pageHref(page))}" data-athos-page="${page}" class="pagination__item ${classes}" aria-label="${escapeHtml(aria)}">${label}</a></li>`;

      const items = pages
        .map((page) => {
          if (page === '…') return `<li><span class="athos-search__pagination-ellipsis" aria-hidden="true">…</span></li>`;
          if (page === current) {
            return `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current" aria-current="page" aria-label="${escapeHtml(this.strings.page.replace('__NUMBER__', page))}">${page}</a></li>`;
          }
          return link(page, page, '', this.strings.page.replace('__NUMBER__', page));
        })
        .join('');

      const prev = current > 1 ? link(current - 1, `${this.strings.caretIcon || ''}<span class="visually-hidden">${escapeHtml(this.strings.previous)}</span>`, 'pagination__item--previous pagination__item-arrow motion-reduce', this.strings.previous) : '';
      const next = current < total ? link(current + 1, `${this.strings.caretIcon || ''}<span class="visually-hidden">${escapeHtml(this.strings.next)}</span>`, 'pagination__item--next pagination__item-arrow motion-reduce', this.strings.next) : '';

      this.paginationEl.innerHTML = `
        <div class="pagination-wrapper">
          <nav class="pagination" role="navigation" aria-label="${escapeHtml(this.strings.paginationLabel)}">
            <ul class="pagination__list list-unstyled flex items-center flex-wrap justify-center" role="list">${prev}${items}${next}</ul>
          </nav>
        </div>`;
    }

    pageHref(page) {
      const params = this.currentParams();
      if (page > 1) params.set('page', page);
      else params.delete('page');
      return `${window.location.pathname}?${params.toString()}`;
    }

    renderEmpty(searched, q) {
      this.gridEl.innerHTML = '';
      this.gridEl.hidden = true;
      this.toolbarEl.hidden = true;
      this.paginationEl.innerHTML = '';
      this.emptyEl.hidden = !searched;
      if (searched) {
        this.emptyEl.innerHTML = `
          <h4>${escapeHtml(this.strings.noResults.replace('__TERMS__', q))}</h4>
          <p class="m-0">${escapeHtml(this.strings.noResultsMessage)}</p>`;
      }
    }

    renderError() {
      this.emptyEl.hidden = false;
      this.gridEl.hidden = true;
      this.emptyEl.innerHTML = `<p class="m-0">${escapeHtml(this.strings.error)}</p>`;
    }

    /* ---------- Events ---------- */

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.dataset.facet) return;
      if (details.open) this.openFacets.add(details.dataset.facet);
      else this.openFacets.delete(details.dataset.facet);
    }

    onChange(event) {
      // filter-list changes are handled by the listener on the list itself (it works inside the drawer too)
      if (event.currentTarget === this && this.facetsEl.contains(event.target)) return;
      const target = event.target;

      if (target.matches('[data-athos-sort]')) {
        const params = this.currentParams();
        [...params.keys()].filter((key) => key.startsWith('sort.')).forEach((key) => params.delete(key));
        if (target.value) {
          const [field, direction] = target.value.split(':');
          params.set(`sort.${field}`, direction);
        }
        params.delete('page');
        this.navigate(params);
        return;
      }

      if (target.closest('[data-athos-facet-form]')) {
        if (target.matches('[data-single]') && target.checked) {
          this.facetsEl.querySelectorAll(`[data-single][name="${CSS.escape(target.name)}"]`).forEach((input) => {
            if (input !== target) input.checked = false;
          });
        }
        this.applyFacetForm();
      }
    }

    applyFacetForm() {
      const params = this.currentParams();
      [...params.keys()].filter((key) => key.startsWith('filter.')).forEach((key) => params.delete(key));

      this.facetsEl.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        if (input.dataset.low !== undefined) {
          params.set(`filter.${input.name.replace(/^filter\./, '').replace(/\.range$/, '')}.low`, input.dataset.low);
          params.set(`filter.${input.name.replace(/^filter\./, '').replace(/\.range$/, '')}.high`, input.dataset.high);
        } else {
          params.append(input.name, input.value);
        }
      });

      this.facetsEl.querySelectorAll('input[data-range]').forEach((input) => {
        if (input.value !== '') params.set(`filter.${input.dataset.field}.${input.dataset.range}`, input.value);
      });

      params.delete('page');
      this.navigate(params, { scroll: false });
    }

    onClick(event) {
      const pageLink = event.target.closest('[data-athos-page]');
      if (pageLink) {
        event.preventDefault();
        const params = this.currentParams();
        const page = parseInt(pageLink.dataset.athosPage, 10);
        if (page > 1) params.set('page', page);
        else params.delete('page');
        this.navigate(params);
        return;
      }

      const remove = event.target.closest('[data-athos-remove]');
      if (remove) {
        const params = this.currentParams();
        const { field, value } = remove.dataset;
        const key = `filter.${field}`;
        const kept = params.getAll(key).filter((v) => v !== value);
        params.delete(key);
        kept.forEach((v) => params.append(key, v));
        // range filters live under .low / .high
        params.delete(`${key}.low`);
        params.delete(`${key}.high`);
        params.delete('page');
        this.navigate(params, { scroll: false });
        return;
      }

      if (event.target.closest('[data-athos-clear-all]')) {
        const params = this.currentParams();
        [...params.keys()].filter((key) => key.startsWith('filter.')).forEach((key) => params.delete(key));
        params.delete('page');
        this.navigate(params, { scroll: false });
        return;
      }
    }
  }

  customElements.define('athos-search', AthosSearch);
}
